import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "wouter";
import {
  ArrowLeft,
  Bookmark,
  ChevronDown,
  ChevronUp,
  Check,
  Download,
  ExternalLink,
  MessageSquareText,
  Pencil,
  Save,
  Search,
  Star,
  Trash2,
} from "lucide-react";
import { api } from "./api";
import { Loader, PageIntro } from "./components";
import { useApp } from "./context";
import type { ReaderMarker } from "./types";

type ConversationItem = {
  text: string;
  date: string;
  own: boolean;
  attachment?: string;
  attachmentLabel?: string;
};

type ReaderState = {
  progress: { position: number; updated_at: string } | null;
  markers: ReaderMarker[];
};

function safeExternalUrl(value: string | null) {
  if (!value || !/^https?:\/\//i.test(value)) return "";
  try {
    const parsed = new URL(value, window.location.href);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
}

function textWithLineBreaks(element: Element | null) {
  if (!element) return "";
  let output = "";
  const blocks = new Set(["DIV", "P", "LI", "SECTION", "ARTICLE"]);
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      output += node.nodeValue || "";
      return;
    }
    if (!(node instanceof Element)) return;
    if (node.tagName === "BR") {
      output += "\n";
      return;
    }
    const block = blocks.has(node.tagName);
    if (block && output && !output.endsWith("\n")) output += "\n";
    node.childNodes.forEach(walk);
    if (block && !output.endsWith("\n")) output += "\n";
  };
  element.childNodes.forEach(walk);
  return output
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const search = query.trim();
  if (!search) return <>{text}</>;
  const lowerText = text.toLocaleLowerCase("cs");
  const lowerSearch = search.toLocaleLowerCase("cs");
  const parts: Array<{ value: string; match: boolean }> = [];
  let cursor = 0;
  while (cursor < text.length) {
    const found = lowerText.indexOf(lowerSearch, cursor);
    if (found < 0) {
      parts.push({ value: text.slice(cursor), match: false });
      break;
    }
    if (found > cursor) parts.push({ value: text.slice(cursor, found), match: false });
    parts.push({ value: text.slice(found, found + search.length), match: true });
    cursor = found + search.length;
  }
  return (
    <>
      {parts.map((part, index) => part.match
        ? <mark key={index}>{part.value}</mark>
        : <span key={index}>{part.value}</span>)}
    </>
  );
}

function parseConversation(source: string, fileName: string): ConversationItem[] {
  if (/\.html?$/i.test(fileName) || /^\s*<!doctype html/i.test(source)) {
    const parsed = new DOMParser().parseFromString(source, "text/html");
    parsed.querySelectorAll("script,style,iframe,object,embed").forEach((element) => element.remove());
    const exportedMessages = Array.from(parsed.querySelectorAll<HTMLElement>(".m__i"));
    if (exportedMessages.length) {
      return exportedMessages.flatMap((element) => {
        const text = textWithLineBreaks(element.querySelector(".message"));
        const date = element.querySelector(".mt")?.textContent?.trim() || "";
        const media = element.querySelector<HTMLAnchorElement>("a[href]")
          || element.querySelector<HTMLImageElement | HTMLVideoElement | HTMLAudioElement>("img[src],video[src],audio[src]");
        const href = safeExternalUrl(media instanceof HTMLAnchorElement ? media.href : media?.getAttribute("src") || "");
        if (!text && !href) return [];
        const mediaElement = element.querySelector("img,video,audio");
        const attachmentLabel = mediaElement?.tagName === "VIDEO"
          ? "Video"
          : mediaElement?.tagName === "AUDIO"
            ? "Audio"
            : "Obrázek nebo GIF";
        return [{
          text: text || attachmentLabel,
          date,
          own: element.classList.contains("m__i--u"),
          attachment: href || undefined,
          attachmentLabel: href ? attachmentLabel : undefined,
        }];
      });
    }
    return Array.from(parsed.querySelectorAll("p,li,article"))
      .map((element) => textWithLineBreaks(element))
      .filter(Boolean)
      .map((text) => ({ text, date: "", own: false }));
  }
  return source
    .split(/\r?\n/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => ({ text, date: "", own: false }));
}

export default function ConversationReader() {
  const { id } = useParams();
  const { content, user, loading } = useApp();
  const document = useMemo(() => content?.documents.find((item) => item.id === id), [content, id]);
  const [items, setItems] = useState<ConversationItem[]>([]);
  const [readerState, setReaderState] = useState<ReaderState>({ progress: null, markers: [] });
  const [status, setStatus] = useState("Načítám konverzaci…");
  const [currentPosition, setCurrentPosition] = useState(0);
  const [saved, setSaved] = useState(false);
  const [pendingMarker, setPendingMarker] = useState<{ position: number; excerpt: string } | null>(null);
  const [markerLabel, setMarkerLabel] = useState("");
  const [editingMarkerId, setEditingMarkerId] = useState<string | null>(null);
  const [editingMarkerLabel, setEditingMarkerLabel] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCursor, setSearchCursor] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const restored = useRef(false);
  const searchMatches = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase("cs");
    if (!query) return [];
    return items.flatMap((item, index) =>
      item.text.toLocaleLowerCase("cs").includes(query) ? [index] : []);
  }, [items, searchQuery]);

  useEffect(() => {
    if (!document) return;
    let cancelled = false;
    Promise.all([
      fetch(document.url).then((response) => {
        if (!response.ok) throw new Error("Soubor konverzace se nepodařilo načíst.");
        return response.text();
      }),
      api<ReaderState>(`/reader/${document.id}`),
    ])
      .then(([source, state]) => {
        if (cancelled) return;
        const parsed = parseConversation(source, document.file_name);
        setItems(parsed);
        setReaderState(state);
        setCurrentPosition(state.progress?.position || 0);
        setStatus(parsed.length ? "" : "V souboru se nepodařilo najít čitelný obsah.");
      })
      .catch((cause) => !cancelled && setStatus(cause instanceof Error ? cause.message : "Načtení selhalo."));
    return () => {
      cancelled = true;
    };
  }, [document]);

  useEffect(() => {
    if (!items.length || restored.current) return;
    restored.current = true;
    window.setTimeout(() => scrollToPosition(readerState.progress?.position || 0), 80);
  }, [items, readerState.progress]);

  useEffect(() => {
    setSearchCursor(0);
    if (!searchMatches.length) return;
    const timeout = window.setTimeout(() => scrollToPosition(searchMatches[0]), 80);
    return () => window.clearTimeout(timeout);
  }, [searchQuery, searchMatches]);

  if (loading || !content) return <Loader />;
  if (!document) {
    return <div className="not-found"><span>404</span><h1>Soubor nebyl nalezen.</h1><Link className="button dark" href="/plot-prog">Zpět do archivu</Link></div>;
  }

  function scrollToPosition(position: number) {
    const container = scrollRef.current;
    const target = container?.querySelector<HTMLElement>(`[data-position="${position}"]`);
    if (container && target) {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTo({ top: Math.max(0, top - 18), behavior: "smooth" });
    }
  }

  function onScroll() {
    const container = scrollRef.current;
    if (!container || !items.length) return;
    const messageElements = Array.from(container.querySelectorAll<HTMLElement>("[data-position]"));
    const threshold = container.scrollTop + 80;
    let position = 0;
    for (const element of messageElements) {
      if (element.offsetTop <= threshold) position = Number(element.dataset.position || 0);
      else break;
    }
    setCurrentPosition(position);
    setSaved(false);
  }

  async function saveProgress(position = currentPosition, silent = false) {
    if (!user) return;
    setCurrentPosition(position);
    await api(`/reader/${document!.id}/progress`, {
      method: "PUT",
      body: JSON.stringify({ position }),
    });
    setReaderState((current) => ({
      ...current,
      progress: { position, updated_at: new Date().toISOString() },
    }));
    if (!silent) {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    }
  }

  function moveSearch(direction: number) {
    if (!searchMatches.length) return;
    const next = (searchCursor + direction + searchMatches.length) % searchMatches.length;
    setSearchCursor(next);
    scrollToPosition(searchMatches[next]);
  }

  function requestMarker(position: number, excerpt: string) {
    if (!user) return;
    const existing = readerState.markers.find((marker) => marker.position === position);
    if (existing) {
      setEditingMarkerId(existing.id);
      setEditingMarkerLabel(existing.label);
      return;
    }
    setPendingMarker({ position, excerpt });
    setMarkerLabel("");
  }

  async function addMarker(event: FormEvent) {
    event.preventDefault();
    if (!user || !pendingMarker || !markerLabel.trim()) return;
    const marker = await api<ReaderMarker>(`/reader/${document!.id}/markers`, {
      method: "POST",
      body: JSON.stringify({
        position: pendingMarker.position,
        label: markerLabel.trim(),
        excerpt: pendingMarker.excerpt,
      }),
    });
    setReaderState((current) => ({ ...current, markers: [...current.markers, marker].sort((a, b) => a.position - b.position) }));
    setPendingMarker(null);
    setMarkerLabel("");
  }

  async function renameMarker(event: FormEvent, markerId: string) {
    event.preventDefault();
    const label = editingMarkerLabel.trim();
    if (!label) return;
    await api(`/reader/${document!.id}/markers/${markerId}`, {
      method: "PUT",
      body: JSON.stringify({ label }),
    });
    setReaderState((current) => ({
      ...current,
      markers: current.markers.map((marker) => marker.id === markerId ? { ...marker, label } : marker),
    }));
    setEditingMarkerId(null);
    setEditingMarkerLabel("");
  }

  async function removeMarker(markerId: string) {
    await api(`/reader/${document!.id}/markers/${markerId}`, { method: "DELETE" });
    setReaderState((current) => ({ ...current, markers: current.markers.filter((marker) => marker.id !== markerId) }));
  }

  const progressPercent = items.length ? Math.round(((currentPosition + 1) / items.length) * 100) : 0;

  return (
    <div className="page-shell reader-page">
      <Link href="/plot-prog" className="detail-back"><ArrowLeft size={16} /> Zpět do Plot&amp;Prog</Link>
      <PageIntro
        eyebrow="Čtečka konverzace"
        title={document.title}
        text={`${items.length.toLocaleString("cs-CZ")} zpráv · rozečteno ${progressPercent} %`}
        action={<a className="button ghost" href={document.url} download={document.file_name}><Download size={17} /> Stáhnout originál</a>}
      />
      {!user && (
        <div className="reader-login-note">
          Pro ukládání rozečteného místa a soukromých značek se <Link href="/prihlaseni">přihlaste</Link>.
        </div>
      )}
      <div className="reader-layout">
        <section className="conversation-panel">
          <div className="reader-toolbar">
            <span><MessageSquareText size={18} /> {progressPercent} %</span>
            {user && (
              <button className="button compact ghost" onClick={() => saveProgress()}>
                {saved ? <Check size={16} /> : <Bookmark size={16} />}
                {saved ? "Uloženo" : "Uložit místo"}
              </button>
            )}
          </div>
          <div className="reader-search">
            <Search size={18} />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  moveSearch(event.shiftKey ? -1 : 1);
                }
              }}
              placeholder="Hledat ve zprávách…"
              aria-label="Hledat ve zprávách"
            />
            <span>{searchQuery.trim() ? `${searchMatches.length ? searchCursor + 1 : 0} / ${searchMatches.length}` : ""}</span>
            <button type="button" onClick={() => moveSearch(-1)} disabled={!searchMatches.length} aria-label="Předchozí výsledek"><ChevronUp /></button>
            <button type="button" onClick={() => moveSearch(1)} disabled={!searchMatches.length} aria-label="Další výsledek"><ChevronDown /></button>
          </div>
          <div className="reader-progress"><span style={{ width: `${progressPercent}%` }} /></div>
          <div className="conversation-scroll" ref={scrollRef} onScroll={onScroll}>
            {status && <div className="reader-status">{status}</div>}
            {items.map((item, index) => {
              const marked = readerState.markers.some((marker) => marker.position === index);
              const readingPoint = readerState.progress?.position === index;
              return (
                <article
                  key={`${index}-${item.date}`}
                  data-position={index}
                  className={`conversation-message ${item.own ? "own" : "received"}${marked ? " marked" : ""}`}
                >
                  <div className="message-bubble">
                    <p><HighlightedText text={item.text} query={searchQuery} /></p>
                    {item.attachment && (
                      <a href={item.attachment} target="_blank" rel="noreferrer">
                        <ExternalLink size={14} /> {item.attachmentLabel}
                      </a>
                    )}
                    {item.date && <time>{item.date}</time>}
                  </div>
                  {user && (
                    <div className="message-actions">
                      <button
                        type="button"
                        className={`bookmark-message${readingPoint ? " active" : ""}`}
                        onClick={() => saveProgress(index)}
                        aria-label={readingPoint ? "Zde máte uložené rozečtené místo" : "Skončil/a jsem číst tady"}
                        title={readingPoint ? "Zde máte uložené rozečtené místo" : "Skončil/a jsem číst tady"}
                      >
                        <Bookmark size={15} fill={readingPoint ? "currentColor" : "none"} />
                      </button>
                      <button
                        type="button"
                        className="mark-message"
                        onClick={() => requestMarker(index, item.text)}
                        aria-label={marked ? "Moment je označený" : "Označit jako zajímavý"}
                        title={marked ? "Moment je označený" : "Označit jako zajímavý"}
                      >
                        <Star size={15} fill={marked ? "currentColor" : "none"} />
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
        <aside className="reader-bookmarks">
          <div>
            <p className="eyebrow">Jen pro vás</p>
            <h2>Záložky</h2>
          </div>
          {readerState.progress && (
            <button className="saved-progress" onClick={() => scrollToPosition(readerState.progress!.position)}>
              <Bookmark size={17} />
              <span><strong>Rozečtené místo</strong><small>Zpráva {readerState.progress.position + 1}</small></span>
            </button>
          )}
          {pendingMarker && (
            <form className="marker-composer" onSubmit={addMarker}>
              <label>
                Pojmenovat zajímavý moment
                <input
                  value={markerLabel}
                  onChange={(event) => setMarkerLabel(event.target.value)}
                  placeholder="Např. Důležitý rozhovor"
                  autoFocus
                  required
                />
              </label>
              <div>
                <button className="button dark compact" type="submit"><Save size={14} /> Uložit</button>
                <button className="button ghost compact" type="button" onClick={() => setPendingMarker(null)}>Zrušit</button>
              </div>
            </form>
          )}
          <div className="marker-list">
            {readerState.markers.map((marker) => (
              <div key={marker.id} className="marker-item">
                {editingMarkerId === marker.id ? (
                  <form className="marker-rename-form" onSubmit={(event) => renameMarker(event, marker.id)}>
                    <input
                      value={editingMarkerLabel}
                      onChange={(event) => setEditingMarkerLabel(event.target.value)}
                      autoFocus
                      required
                    />
                    <button type="submit" aria-label="Uložit nový název"><Check size={14} /></button>
                    <button type="button" onClick={() => setEditingMarkerId(null)} aria-label="Zrušit">×</button>
                  </form>
                ) : (
                  <>
                    <button onClick={() => scrollToPosition(marker.position)}>
                      <Star size={15} fill="currentColor" />
                      <span><strong>{marker.label}</strong><small>{marker.excerpt}</small></span>
                    </button>
                    <button
                      onClick={() => {
                        setEditingMarkerId(marker.id);
                        setEditingMarkerLabel(marker.label);
                      }}
                      aria-label="Přejmenovat značku"
                    >
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => removeMarker(marker.id)} aria-label="Odstranit značku"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
          {user && !readerState.markers.length && <p className="reader-empty">Hvězdičkou u zprávy si označíte zajímavý moment. Nikdo jiný ho neuvidí.</p>}
        </aside>
      </div>
    </div>
  );
}
